import { useEffect, useMemo, useRef, useState } from "react";
import { superAdminApi, type User, type Role, type Tenant } from "./api";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Pencil,
  ClipboardList,
  FileText,
  Trash2,
  Power,
  RotateCcw,
  Phone,
  Search,
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSearchParams } from "react-router-dom";
import { API_V1_BASE_URL } from "../../config/api";

interface PagePermission {
  id: string;
  pageName: string;
  path: string;
  icon: string;
  description: string;
  allowedRoles: string[];
}

interface RolePermission {
  role: string;
  name: string;
  description: string;
  permissions: string[];
  pages: string[];
}

interface Permission {
  permission_code: string;
  module: string;
  description: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const IconButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  colorClass: string;
}> = ({ icon, label, onClick, colorClass }) => (
  <div className="relative group">
    <button
      onClick={onClick}
      aria-label={label}
      className={`p-2 rounded-lg transition ${colorClass}`}
    >
      {icon}
    </button>
    <span
      className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2
                 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white
                 opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100
                 transition-all duration-150 z-10"
    >
      {label}
    </span>
  </div>
);

const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}> = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}) => {
    const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalItems);

    return (
      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-1 py-0.5"
          >
            {[8, 10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <div className="text-sm text-gray-600">
          {totalItems === 0 ? "0 results" : `${start}-${end} of ${totalItems}`}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-1.5 py-1 border rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            Prev
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-1.5 py-1 border rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

const Section: React.FC<{
  icon: string;
  iconBg: string;
  iconText: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ icon, iconBg, iconText, title, subtitle, children }) => (
  <section className="border border-gray-200 rounded-2xl p-5 bg-white">
    <div className="flex items-center gap-2 mb-1">
      <div
        className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center ${iconText} text-sm`}
      >
        {icon}
      </div>
      <h3 className="text-base font-bold text-gray-900">{title}</h3>
    </div>
    {subtitle && <p className="text-xs text-gray-500 mb-4 ml-9">{subtitle}</p>}
    {!subtitle && <div className="mb-4" />}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </section>
);

const TenantUsers: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeView =
    (searchParams.get("view") as "users" | "tenants" | "roles") || "users";

  const [users, setUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<Role[]>([]);
  const [allFilterRoles, setAllFilterRoles] = useState<Role[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [selectedAuditUser, setSelectedAuditUser] = useState<User | null>(null);
  const [isUserAudit, setIsUserAudit] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [tenantSearchTerm, setTenantSearchTerm] = useState("");
  const [usersPerPage, setUsersPerPage] = useState(8);
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [tenantStatusFilter, setTenantStatusFilter] = useState("all");
  const [tenantsPerPage, setTenantsPerPage] = useState(8);
  const [currentTenantPage, setCurrentTenantPage] = useState(1);

  // User Pagination State
  const [userTotal, setUserTotal] = useState(0);
  const [userTotalPages, setUserTotalPages] = useState(0);

  // Role Pagination State
  const [roleTotal, setRoleTotal] = useState(0);
  const [roleTotalPages, setRoleTotalPages] = useState(0);

  // Tenant Pagination State
  const [tenantTotal, setTenantTotal] = useState(0);
  const [tenantTotalPages, setTenantTotalPages] = useState(0);
  const [tenantLoading, setTenantLoading] = useState(false);

  // User Stats State (always unfiltered — independent of statusFilter/roleFilter/tenantFilter)
  const [userStats, setUserStats] = useState({
    total: 0,
    active: 0,
    deactivated: 0,
    pending: 0,
  });

  const pagePermissions: PagePermission[] = [
    {
      id: "dashboard",
      pageName: "Dashboard",
      path: "/dashboard",
      icon: "📊",
      description: "View main dashboard and analytics",
      allowedRoles: ["customer_admin", "customer_manager", "customer_viewer"],
    },
    {
      id: "campaigns",
      pageName: "Campaigns",
      path: "/campaigns",
      icon: "📢",
      description: "View and manage marketing campaigns",
      allowedRoles: ["customer_admin", "customer_manager"],
    },
    {
      id: "create-campaign",
      pageName: "Create Campaign",
      path: "/campaigns/create",
      icon: "➕",
      description: "Create new marketing campaigns",
      allowedRoles: ["customer_admin", "customer_manager"],
    },
    {
      id: "edit-campaign",
      pageName: "Edit Campaign",
      path: "/campaigns/edit/*",
      icon: "✏️",
      description: "Edit existing campaigns",
      allowedRoles: ["customer_admin", "customer_manager"],
    },
    {
      id: "delete-campaign",
      pageName: "Delete Campaign",
      path: "/campaigns/delete",
      icon: "🗑️",
      description: "Delete campaigns",
      allowedRoles: ["customer_admin"],
    },
    {
      id: "user-management",
      pageName: "User Management",
      path: "/users",
      icon: "👥",
      description: "Manage internal users",
      allowedRoles: ["customer_admin"],
    },
  ];

  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [tenantFilter, setTenantFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showCreateTenantModal, setShowCreateTenantModal] = useState(false);
  const [tenantModalStep, setTenantModalStep] = useState<
    "choose" | "create" | "existing"
  >("choose");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [tenantEditForm, setTenantEditForm] = useState({
    name: "",
    subdomain: "",
    email: "",
    phoneNumber: "",
    status: "",
    maxUsers: "",
    logoUrl: "",
    primaryColor: "#2563EB",
    secondaryColor: "#F97316",
    themeMode: "CUSTOM",
    storageLimitValue: "",
    storageLimitUnit: "GB",
  });
  const [tenantForm, setTenantForm] = useState({
    tenant: {
      name: "",
      subdomain: "",
      email: "",
      phoneNumber: "",
      maxUsers: "",
      logoUrl: "",
      primaryColor: "#2563EB",
      secondaryColor: "#F97316",
      themeMode: "CUSTOM",
      storageLimitValue: "",
      storageLimitUnit: "GB",
    },
    user: {
      firstName: "",
      lastName: "",
      email: "",
      role: "CUSTOMER_ADMIN",
      department: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
  });

  // Role management states
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [roleCurrentPage, setRoleCurrentPage] = useState(1);
  const [roleItemsPerPage, setRoleItemsPerPage] = useState(10);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [roleForm, setRoleForm] = useState({
    name: "",
    description: "",
    permissions: [] as string[],
  });
  const [showPermissionError, setShowPermissionError] = useState(false);
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [editRoleForm, setEditRoleForm] = useState({
    name: "",
    description: "",
    isActive: true,
  });
  const [updatingRole, setUpdatingRole] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [creatingRole, setCreatingRole] = useState(false);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // ---- Existing-tenant "add user" flow ----
  const [tenantOptions, setTenantOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [allFilterTenants, setAllFilterTenants] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
  const [tenantStatusDropdownOpen, setTenantStatusDropdownOpen] =
    useState(false);
  const [tenantOptionsLoading, setTenantOptionsLoading] = useState(false);
  const [existingSubmitting, setExistingSubmitting] = useState(false);
  const [existingForm, setExistingForm] = useState({
    tenantId: "",
    role: "CUSTOMER_MANAGER",
    firstName: "",
    lastName: "",
    email: "",
    department: "",
    phoneNumber: "",
  });

  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    role: "",
    department: "",
    phoneNumber: "",
    status: "",
  });

  const [selectedRolePermissions, setSelectedRolePermissions] =
    useState<RolePermission | null>(null);

  // Debounce timers
  const userSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const roleSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const tenantSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const roleDropdownRef = useRef<HTMLDivElement | null>(null);
  const statusDropdownRef = useRef<HTMLDivElement | null>(null);
  const tenantStatusDropdownRef = useRef<HTMLDivElement | null>(null);
  const tenantDropdownRef = useRef<HTMLDivElement | null>(null);
  const safeUsers = Array.isArray(users) ? users : [];
  const masterUsers = Array.isArray(allUsers) ? allUsers : [];

  // Stats are always unfiltered totals — independent of the table's status/role/tenant filters
  const stats = [
    {
      title: "Total Users",
      value: userStats.total,
      icon: "👥",
      color: "text-indigo-600",
    },
    {
      title: "Total Tenants",
      value: tenantTotal || tenants.length,
      icon: "🏢",
      color: "text-blue-600",
    },
    {
      title: "Active Users",
      value: userStats.active,
      icon: "✅",
      color: "text-green-600",
    },
    {
      title: "Deactivated Users",
      value: userStats.deactivated,
      icon: "⛔",
      color: "text-red-600",
    },
  ];

  const hasActiveFilters =
    statusFilter !== "all" ||
    roleFilter !== "all" ||
    tenantFilter !== "all" ||
    searchTerm.trim() !== "";

  const clearFilters = () => {
    setStatusFilter("all");
    setRoleFilter("all");
    setTenantFilter("all");
    setSearchTerm("");
  };

  const hasActiveTenantFilters =
    tenantStatusFilter !== "all" || tenantSearchTerm.trim() !== "";

  const clearTenantFilters = () => {
    setTenantStatusFilter("all");
    setTenantSearchTerm("");
  };

  // Server already applies status + search, so don't re-filter the page here.
  const filteredTenants = tenants;

  const totalTenantPages = Math.max(
    1,
    tenantTotalPages || Math.ceil(filteredTenants.length / tenantsPerPage),
  );

  const paginatedTenants = filteredTenants;

  const fetchAllFilterRoles = async () => {
    try {
      const response = await fetch(
        `${API_V1_BASE_URL}/admin/roles?limit=1000`,
        {
          headers: getAuthHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch roles: ${response.status}`);
      }

      const json: any = await response.json();

      if (json.success && json.data) {
        const rawRoles: any[] = Array.isArray(json.data.data)
          ? json.data.data
          : Array.isArray(json.data)
            ? json.data
            : [];

        const mapped: Role[] = rawRoles.map((r) => ({
          id: r.id,
          tenantId: "",
          role: r.role_name || r.role,
          name: r.role_name || r.name,
          description: r.description,
          permissions: Array.isArray(r.permissions) ? r.permissions : [],
          pages: Array.isArray(r.pages) ? r.pages : [],
        }));

        setAllFilterRoles(mapped);
      }
    } catch (error) {
      console.error("Failed to load filter roles:", error);
    }
  };

  // "Admin" also covers Super Admins. Any other role maps to itself.
  const getRoleFilterValues = (filter: string): string[] => {
    if (filter === "all") return [];
    if (filter.trim().toUpperCase() === "ADMIN") return [filter, "SUPER_ADMIN"];
    return [filter];
  };

  const filteredUsers = useMemo(() => {
    // With server-side pagination, users already contains the current page
    // But we also filter client-side for the current page data
    return safeUsers.filter((user) => {
      const matchesStatus =
        statusFilter === "all" ||
        user.status.toLowerCase() === statusFilter.toLowerCase();

      const matchesRole =
        roleFilter === "all" ||
        getRoleFilterValues(roleFilter).some(
          (r) => r.toUpperCase() === (user.role || "").toUpperCase(),
        );

      const matchesTenant =
        tenantFilter === "all" || user.tenant_id === tenantFilter;

      const search = searchTerm.toLowerCase();

      const matchesSearch =
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search) ||
        (user.tenant_email || "").toLowerCase().includes(search) ||
        (user.tenant_name || "").toLowerCase().includes(search) ||
        (user.department || "").toLowerCase().includes(search);

      return matchesStatus && matchesRole && matchesTenant && matchesSearch;
    });
  }, [users, statusFilter, roleFilter, tenantFilter, searchTerm]);

  const totalUserPages = Math.max(
    1,
    userTotalPages || Math.ceil(filteredUsers.length / usersPerPage),
  );

  const paginatedUsers = filteredUsers;

  const safeRoles = Array.isArray(roles) ? roles : [];
  const filteredRoles = useMemo(() => {
    // With server-side pagination, roles already contains the current page
    // But we also filter client-side for the current page data
    const search = (roleSearchTerm ?? "").toLowerCase();
    return safeRoles.filter((r) => {
      const name = (r.name ?? "").toLowerCase();
      const role = (r.role ?? "").toLowerCase();
      const description = (r.description ?? "").toLowerCase();

      return (
        name.includes(search) ||
        role.includes(search) ||
        description.includes(search)
      );
    });
  }, [safeRoles, roleSearchTerm]);

  useEffect(() => {
    setRoleCurrentPage(1);
  }, [roleSearchTerm]);

  const paginatedRoles = filteredRoles;
  const safePermissions = Array.isArray(allPermissions) ? allPermissions : [];
  const filteredPermissions = useMemo(() => {
    const search = permissionSearch.toLowerCase();
    return safePermissions.filter(
      (p) =>
        (p.permission_code ?? "").toLowerCase().includes(search) ||
        (p.module ?? "").toLowerCase().includes(search) ||
        (p.description ?? "").toLowerCase().includes(search),
    );
  }, [safePermissions, permissionSearch]);

  const allFilteredSelected =
    filteredPermissions.length > 0 &&
    filteredPermissions.every((p) =>
      roleForm.permissions.includes(p.permission_code),
    );

  const toggleSelectAllPermissions = () => {
    setRoleForm((prev) => {
      const filteredIds = filteredPermissions.map((p) => p.permission_code);

      if (allFilteredSelected) {
        return {
          ...prev,
          permissions: prev.permissions.filter(
            (id) => !filteredIds.includes(id),
          ),
        };
      }

      return {
        ...prev,
        permissions: [...new Set([...prev.permissions, ...filteredIds])],
      };
    });
  };

  const togglePermission = (permId: string) => {
    setRoleForm((prev) => {
      const permissions = prev.permissions.includes(permId)
        ? prev.permissions.filter((p) => p !== permId)
        : [...prev.permissions, permId];

      if (permissions.length > 0) {
        setShowPermissionError(false);
      }

      return {
        ...prev,
        permissions,
      };
    });
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "SUPER_ADMIN":
        return "bg-red-100 text-red-700";
      case "CUSTOMER_ADMIN":
        return "bg-purple-100 text-purple-800";
      case "CUSTOMER_MANAGER":
        return "bg-blue-100 text-blue-800";
      case "CUSTOMER_VIEWER":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "SUPER_ADMIN":
        return "👑";
      case "CUSTOMER_ADMIN":
        return "👑";
      case "CUSTOMER_MANAGER":
        return "📊";
      case "CUSTOMER_VIEWER":
        return "👤";
      default:
        return "👤";
    }
  };

  const viewRolePermissions = (role: string) => {
    const roleData = roles.find((r) => r.role === role);
    if (!roleData) return;
    setSelectedRolePermissions({
      role: roleData.role,
      name: roleData.name,
      description: roleData.description,
      permissions: roleData.permissions,
      pages: roleData.pages,
    });
    setShowPermissionsModal(true);
  };

  // ===== SERVER-SIDE PAGINATED API CALLS =====

  // Fetch Users with pagination
  const fetchUsers = async (
    page: number = currentUserPage,
    limit: number = usersPerPage,
  ) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      // Only add filters if they're not "all"
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (roleFilter !== "all") {
        params.append("role", getRoleFilterValues(roleFilter).join(","));
      }
      if (tenantFilter !== "all") {
        params.append("tenantId", tenantFilter);
      }
      if (searchTerm && searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      const response = await fetch(
        `${API_V1_BASE_URL}/admin/users?${params.toString()}`,
        {
          headers: getAuthHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const json: any = await response.json();

      if (json.success && json.data) {
        const paginated = json.data as PaginatedResponse<any>;
        const usersList: User[] = paginated.data || [];
        setUsers(usersList);
        setUserTotal(paginated.total || 0);
        setUserTotalPages(paginated.totalPages || 0);
        setCurrentUserPage(paginated.page || 1);
        setUsersPerPage(paginated.limit || usersPerPage);

        // Store all users for stats when no filters are applied
        if (
          statusFilter === "all" &&
          roleFilter === "all" &&
          tenantFilter === "all" &&
          !searchTerm
        ) {
          setAllUsers(usersList);
        }
      } else {
        // Fallback for non-paginated response
        let usersList: User[] = [];
        if (Array.isArray(json)) {
          usersList = json;
        } else if (Array.isArray(json.data)) {
          usersList = json.data;
        } else if (Array.isArray(json.users)) {
          usersList = json.users;
        } else if (Array.isArray(json.rows)) {
          usersList = json.rows;
        }
        setUsers(usersList);
        setUserTotal(usersList.length);
        setUserTotalPages(Math.ceil(usersList.length / usersPerPage));

        if (
          statusFilter === "all" &&
          roleFilter === "all" &&
          tenantFilter === "all" &&
          !searchTerm
        ) {
          setAllUsers(usersList);
        }
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // Fetch unfiltered user stats for the top cards (independent of table filters)
  const fetchUserStats = async () => {
    try {
      const headers = getAuthHeaders();
      const [totalRes, activeRes, deactivatedRes, pendingRes] =
        await Promise.all([
          fetch(`${API_V1_BASE_URL}/admin/users?page=1&limit=1`, { headers }),
          fetch(`${API_V1_BASE_URL}/admin/users?page=1&limit=1&status=active`, {
            headers,
          }),
          fetch(
            `${API_V1_BASE_URL}/admin/users?page=1&limit=1&status=deactivated`,
            { headers },
          ),
          fetch(
            `${API_V1_BASE_URL}/admin/users?page=1&limit=1&status=pending`,
            { headers },
          ),
        ]);
      const [totalJson, activeJson, deactivatedJson, pendingJson] =
        await Promise.all([
          totalRes.json(),
          activeRes.json(),
          deactivatedRes.json(),
          pendingRes.json(),
        ]);
      setUserStats({
        total: totalJson?.data?.total ?? 0,
        active: activeJson?.data?.total ?? 0,
        deactivated: deactivatedJson?.data?.total ?? 0,
        pending: pendingJson?.data?.total ?? 0,
      });
    } catch (err) {
      console.error("Failed to fetch user stats:", err);
    }
  };

  // Fetch Roles with pagination
  const fetchRoles = async (
    page: number = roleCurrentPage,
    limit: number = roleItemsPerPage,
  ) => {
    try {
      setRolesLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (roleSearchTerm && roleSearchTerm.trim()) {
        params.append("search", roleSearchTerm.trim());
      }

      const response = await fetch(
        `${API_V1_BASE_URL}/admin/roles?${params.toString()}`,
        {
          headers: getAuthHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch roles: ${response.status}`);
      }

      const json: any = await response.json();

      if (json.success && json.data) {
        const paginated = json.data as PaginatedResponse<any>;
        const rawRoles: any[] = paginated.data || [];
        const mapped: Role[] = rawRoles.map((r) => ({
          id: r.id,
          tenantId: "",
          role: r.role_name || r.role,
          name: r.role_name || r.name,
          description: r.description,
          permissions: Array.isArray(r.permissions) ? r.permissions : [],
          pages: Array.isArray(r.pages) ? r.pages : [],
        })) as Role[];

        setRoles(mapped);
        setRoleTotal(paginated.total || 0);
        setRoleTotalPages(paginated.totalPages || 0);
        setRoleCurrentPage(paginated.page || 1);
        setRoleItemsPerPage(paginated.limit || roleItemsPerPage);
      } else {
        // Fallback for non-paginated response
        const rawRoles: any[] = Array.isArray(json.data) ? json.data : [];
        const mapped: Role[] = rawRoles.map((r) => ({
          id: r.id,
          tenantId: "",
          role: r.role_name || r.role,
          name: r.role_name || r.name,
          description: r.description,
          permissions: Array.isArray(r.permissions) ? r.permissions : [],
          pages: Array.isArray(r.pages) ? r.pages : [],
        })) as Role[];
        setRoles(mapped);
        setRoleTotal(mapped.length);
        setRoleTotalPages(Math.ceil(mapped.length / roleItemsPerPage));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load roles.");
    } finally {
      setRolesLoading(false);
    }
  };

  // Fetch Tenants with pagination
  const fetchTenants = async (
    page: number = currentTenantPage,
    limit: number = tenantsPerPage,
  ) => {
    try {
      setTenantLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (tenantStatusFilter !== "all") {
        params.append("status", tenantStatusFilter);
      }
      if (tenantSearchTerm && tenantSearchTerm.trim()) {
        params.append("search", tenantSearchTerm.trim());
      }

      const response = await fetch(
        `${API_V1_BASE_URL}/admin/tenants?${params.toString()}`,
        {
          headers: getAuthHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch tenants: ${response.status}`);
      }

      const json: any = await response.json();

      if (json.success && json.data) {
        const paginated = json.data as PaginatedResponse<any>;
        const list: Tenant[] = paginated.data || [];
        setTenants(list);
        setTenantTotal(paginated.total || 0);
        setTenantTotalPages(paginated.totalPages || 0);
        setCurrentTenantPage(paginated.page || 1);
        setTenantsPerPage(paginated.limit || tenantsPerPage);

        // Update tenant options for dropdowns
        // setTenantOptions(list.map((t) => ({ id: t.id, name: t.name })));
      } else {
        // Fallback for non-paginated response
        let list: Tenant[] = [];
        if (Array.isArray(json)) {
          list = json;
        } else if (Array.isArray(json.data)) {
          list = json.data;
        } else if (Array.isArray(json.tenants)) {
          list = json.tenants;
        }
        setTenants(list);
        setTenantTotal(list.length);
        setTenantTotalPages(Math.ceil(list.length / tenantsPerPage));
        // setTenantOptions(list.map((t) => ({ id: t.id, name: t.name })));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load tenants");
    } finally {
      setTenantLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const response = await fetch(`${API_V1_BASE_URL}/admin/permissions`, {
        headers: getAuthHeaders(),
      });
      const json: any = await response.json();
      setAllPermissions(Array.isArray(json.data) ? json.data : []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load permissions.");
    } finally {
      setPermissionsLoading(false);
    }
  };

  // ===== TENANT THEME (admin-scoped: PUT/GET /theme/:tenantId) =====
  const fetchTenantTheme = async (tenantId: string) => {
    try {
      const response = await fetch(`${API_V1_BASE_URL}/theme/${tenantId}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok)
        throw new Error(`Failed to fetch theme: ${response.status}`);
      const json: any = await response.json();
      return json?.data ?? null;
    } catch (err) {
      console.error("Failed to fetch tenant theme:", err);
      return null; // fall back to defaults already in state
    }
  };


  const saveTenantTheme = async (
    tenantId: string,
    theme: {
      primaryColor: string;
      secondaryColor: string;
      themeMode: string;
    },
  ) => {
    if (!tenantId) {
      throw new Error("Cannot save theme: tenant ID is missing.");
    }

    const response = await fetch(
      `${API_V1_BASE_URL}/theme/${encodeURIComponent(tenantId)}`,
      {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(theme),
      },
    );

    const json = await response.json().catch(() => null);

    if (!response.ok || !json?.success || !json?.data) {
      throw new Error(
        json?.message ||
        `Failed to save theme (HTTP ${response.status})`,
      );
    }

    console.log("Theme saved successfully:", json.data);
    return json.data;
  };


  const openAuditLogs = async (user: User) => {
    try {
      setSelectedAuditUser(user);
      setIsUserAudit(true);
      const response: any = await superAdminApi.getUserAuditLogs(user.id);
      const logs = Array.isArray(response) ? response : (response.data ?? []);
      setAuditLogs(logs);
      setShowAuditModal(true);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load audit logs",
      );
    }
  };

  const saveUser = async () => {
    if (!editingUser) return;
    try {
      await superAdminApi.updateUser(editingUser.id, editForm);
      toast.success("User updated successfully.");
      setShowEditModal(false);
      fetchUsers(currentUserPage);
      fetchUserStats();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update failed");
    }
  };


  const saveTenant = async () => {
    if (!editingTenant) return;

    try {
      // Save the tenant's ordinary fields first.
      await superAdminApi.updateTenant(editingTenant.id, {
        name: tenantEditForm.name,
        subdomain: tenantEditForm.subdomain,
        email: tenantEditForm.email,
        phoneNumber: tenantEditForm.phoneNumber,
        status: tenantEditForm.status,
        maxUsers: Number(tenantEditForm.maxUsers),
        storageLimitValue:
          Number(tenantEditForm.storageLimitValue) || 0,
        storageLimitUnit: tenantEditForm.storageLimitUnit,
        logoUrl: tenantEditForm.logoUrl,
      });

      // Save the colors in the separate theme table.
      await saveTenantTheme(editingTenant.id, {
        primaryColor: tenantEditForm.primaryColor,
        secondaryColor: tenantEditForm.secondaryColor,
        themeMode: tenantEditForm.themeMode,
      });

      toast.success("Tenant and theme saved successfully.");
      setShowEditTenantModal(false);
      await fetchTenants(currentTenantPage);
    } catch (err) {
      console.error("Failed to save tenant or theme:", err);

      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save tenant or theme.",
      );
    }
  };


  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await superAdminApi.deleteUser(userToDelete.id);
      toast.success("User deleted successfully.");
      setShowDeleteModal(false);
      setUserToDelete(null);
      await fetchUsers(currentUserPage);
      await fetchUserStats();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    }
  };

  const createTenant = async () => {
    if (tenantForm.user.password !== tenantForm.user.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setCreatingTenant(true);
    const toastId = toast.loading("Creating tenant...");
    try {
      const response: any = await superAdminApi.createTenant({
        ...tenantForm,
        tenant: {
          ...tenantForm.tenant,
          maxUsers: Number(tenantForm.tenant.maxUsers),
          storageLimitValue: Number(tenantForm.tenant.storageLimitValue) || 0,
          storageLimitUnit: tenantForm.tenant.storageLimitUnit,
        },
      });

      // Persist the selected theme against the newly created tenant.
      // Adjust this path if your createTenant response shape differs.
      const newTenantId =
        response?.data?.tenant?.id ??
        response?.data?.id ??
        response?.tenant?.id;
      if (newTenantId) {
        await saveTenantTheme(newTenantId, {
          primaryColor: tenantForm.tenant.primaryColor,
          secondaryColor: tenantForm.tenant.secondaryColor,
          themeMode: tenantForm.tenant.themeMode,
        });
      }

      setShowCreateTenantModal(false);
      setTenantModalStep("choose");

      setTenantForm({
        tenant: {
          name: "",
          subdomain: "",
          email: "",
          phoneNumber: "",
          maxUsers: "",
          logoUrl: "",
          primaryColor: "#2563EB",
          secondaryColor: "#F97316",
          themeMode: "CUSTOM",
          storageLimitValue: "",
          storageLimitUnit: "GB",
        },
        user: {
          firstName: "",
          lastName: "",
          email: "",
          role: "CUSTOMER_ADMIN",
          department: "",
          phoneNumber: "",
          password: "",
          confirmPassword: "",
        },
      });

      await fetchUsers(currentUserPage);
      await fetchTenants(currentTenantPage);
      await fetchUserStats();

      toast.update(toastId, {
        render:
          "Tenant created successfully. A temporary password has been sent to the administrator's email.",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (err) {
      toast.update(toastId, {
        render: err instanceof Error ? err.message : "Failed to create tenant",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setCreatingTenant(false);
    }
  };

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!tenantForm.tenant.name.trim()) {
      toast.warning("Please enter the company name before uploading a logo.");
      e.target.value = "";
      return;
    }

    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setUploadingLogo(true);
    try {
      const { logoUrl } = await superAdminApi.uploadTenantLogo(
        file,
        tenantForm.tenant.name.trim(),
      );
      setTenantForm((prev) => ({
        ...prev,
        tenant: { ...prev.tenant, logoUrl },
      }));
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Logo upload failed");
      setLogoFile(null);
      setLogoPreviewUrl("");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleEditLogoSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const name = tenantEditForm.name.trim() || editingTenant?.name || "";
    if (!name) {
      toast.warning("Tenant name is missing; cannot upload logo.");
      e.target.value = "";
      return;
    }

    setUploadingLogo(true);
    try {
      const { logoUrl } = await superAdminApi.uploadTenantLogo(file, name);
      setTenantEditForm((prev) => ({ ...prev, logoUrl }));
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const resetRoleForm = () => {
    setRoleForm({
      name: "",
      description: "",
      permissions: [],
    });
    setShowPermissionError(false);
  };

  const createRole = async () => {
    if (!roleForm.name.trim()) {
      toast.warning("Please enter a role name.");
      return;
    }
    if (roleForm.description.trim().length === 0) {
      toast.warning("Please enter a description.");
      return;
    }
    if (roleForm.permissions.length === 0) {
      setShowPermissionError(true);
      return;
    }
    setCreatingRole(true);
    try {
      const response = await fetch(`${API_V1_BASE_URL}/admin/roles`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          roleName: roleForm.name.trim(),
          description: roleForm.description.trim(),
          permissions: roleForm.permissions,
        }),
      });

      const json: any = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json?.message || "Failed to create role");
      }
      toast.success(json.message || "Role registered successfully.");
      setShowCreateRoleModal(false);
      resetRoleForm();
      setPermissionSearch("");
      await fetchRoles(1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create role");
    } finally {
      setCreatingRole(false);
    }
  };

  const openEditRole = (role: Role) => {
    setEditingRole(role);
    setEditRoleForm({
      name: role.role || role.name,
      description: role.description || "",
      isActive: (role as any).isActive ?? true,
    });
    setShowEditRoleModal(true);
  };

  const updateRole = async () => {
    if (!editingRole) return;
    if (!editRoleForm.name.trim()) {
      toast.warning("Please enter a role name.");
      return;
    }
    setUpdatingRole(true);
    try {
      const response = await fetch(
        `${API_V1_BASE_URL}/admin/roles/${editingRole.id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            roleName: editRoleForm.name.trim(),
            description: editRoleForm.description,
            isActive: editRoleForm.isActive,
          }),
        },
      );

      const json: any = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json?.message || "Failed to update role");
      }
      toast.success(json.message || "Role updated successfully.");
      setShowEditRoleModal(false);
      setEditingRole(null);
      await fetchRoles(roleCurrentPage);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setUpdatingRole(false);
    }
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;
    try {
      const response = await fetch(
        `${API_V1_BASE_URL}/admin/roles/${roleToDelete.id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ isActive: false }),
        },
      );
      const json: any = await response.json();
      if (!response.ok || !json.success) {
        throw new Error(json?.message || "Failed to delete role");
      }
      toast.success("Role deleted successfully.");
      setShowDeleteRoleModal(false);
      setRoleToDelete(null);
      await fetchRoles(roleCurrentPage);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete role");
    }
  };

  const resetExistingForm = () =>
    setExistingForm({
      tenantId: "",
      role: "CUSTOMER_MANAGER",
      firstName: "",
      lastName: "",
      email: "",
      department: "",
      phoneNumber: "",
    });

  const createUserInExistingTenant = async () => {
    if (!existingForm.tenantId) {
      toast.warning("Please select a tenant.");
      return;
    }

    setExistingSubmitting(true);
    try {
      await superAdminApi.createUser({
        tenantId: existingForm.tenantId,
        role: existingForm.role,
        firstName: existingForm.firstName,
        lastName: existingForm.lastName,
        email: existingForm.email,
        department: existingForm.department || undefined,
        phoneNumber: existingForm.phoneNumber || undefined,
      } as any);
      toast.success(
        "User created. A temporary password has been emailed to them.",
      );
      setShowCreateTenantModal(false);
      setTenantModalStep("choose");
      resetExistingForm();
      fetchUsers(currentUserPage);
      fetchUserStats();
    } catch (err: any) {
      toast.error(err.message || "Failed to create user");
    } finally {
      setExistingSubmitting(false);
    }
  };

  // ===== EFFECTS FOR SEARCH/FILTER CHANGES =====

  // User search/filter changes -> reset to page 1
  useEffect(() => {
    if (userSearchTimeoutRef.current) {
      clearTimeout(userSearchTimeoutRef.current);
    }
    userSearchTimeoutRef.current = setTimeout(() => {
      fetchUsers(1);
    }, 300);

    return () => {
      if (userSearchTimeoutRef.current) {
        clearTimeout(userSearchTimeoutRef.current);
      }
    };
  }, [statusFilter, roleFilter, tenantFilter, searchTerm]);

  // Role search changes -> reset to page 1
  useEffect(() => {
    if (roleSearchTimeoutRef.current) {
      clearTimeout(roleSearchTimeoutRef.current);
    }
    roleSearchTimeoutRef.current = setTimeout(() => {
      fetchRoles(1);
    }, 300);

    return () => {
      if (roleSearchTimeoutRef.current) {
        clearTimeout(roleSearchTimeoutRef.current);
      }
    };
  }, [roleSearchTerm]);

  // Tenant search/filter changes -> reset to page 1
  useEffect(() => {
    if (tenantSearchTimeoutRef.current) {
      clearTimeout(tenantSearchTimeoutRef.current);
    }
    tenantSearchTimeoutRef.current = setTimeout(() => {
      fetchTenants(1);
    }, 300);

    return () => {
      if (tenantSearchTimeoutRef.current) {
        clearTimeout(tenantSearchTimeoutRef.current);
      }
    };
  }, [tenantStatusFilter, tenantSearchTerm]);

  // Initial loads
  useEffect(() => {
    fetchUsers(1);
    fetchRoles(1);
    fetchAllFilterRoles();
    fetchTenants(1);
    fetchPermissions();
    fetchUserStats();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(e.target as Node)
      ) {
        setRoleDropdownOpen(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(e.target as Node)
      ) {
        setStatusDropdownOpen(false);
      }
      if (
        tenantStatusDropdownRef.current &&
        !tenantStatusDropdownRef.current.contains(e.target as Node)
      ) {
        setTenantStatusDropdownOpen(false);
      }
      if (
        tenantDropdownRef.current &&
        !tenantDropdownRef.current.contains(e.target as Node)
      ) {
        setTenantDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load tenant options for dropdowns
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const list: any = await superAdminApi.getTenants();

        if (Array.isArray(list)) {
          const mappedTenants = list
            .filter((tenant) => tenant?.id && tenant?.name)
            .map((tenant) => ({
              id: tenant.id,
              name: tenant.name,
            }));

          setAllFilterTenants(mappedTenants);

          // Keep this for the existing-tenant user creation flow
          setTenantOptions(mappedTenants);
        }
      } catch (err) {
        console.error("Failed to load tenant filter options:", err);
      }
    };

    loadTenants();
  }, []);

  useEffect(() => {
    if (
      showCreateTenantModal &&
      tenantModalStep === "existing" &&
      tenantOptions.length === 0
    ) {
      setTenantOptionsLoading(true);
      superAdminApi
        .getTenants()
        .then((list: any) => setTenantOptions(Array.isArray(list) ? list : []))
        .catch(() => { })
        .finally(() => setTenantOptionsLoading(false));
    }
  }, [showCreateTenantModal, tenantModalStep, tenantOptions.length]);

  // Determine what to render based on the view param
  const renderContent = () => {
    // For roles view
    if (activeView === "roles") {
      return (
        <>
          {rolesLoading && (
            <div className="mb-4 text-center text-gray-500">
              Loading roles...
            </div>
          )}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm shadow-sm">
                  🛡️
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">
                    Roles & Permissions
                  </h2>
                  <p className="text-xs text-blue-700/70">
                    Manage roles and the permissions assigned to them.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={roleSearchTerm}
                    onChange={(e) => setRoleSearchTerm(e.target.value)}
                    placeholder="Search roles..."
                    className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-56 transition"
                  />
                </div>

                <button
                  onClick={() => {
                    resetRoleForm();
                    setPermissionSearch("");
                    setShowCreateRoleModal(true);
                  }}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition shadow-sm"
                >
                  + Create Role
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-50/60 to-indigo-50/60 border-b border-blue-100">
                    <th className="text-center px-4 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="text-center px-4 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="text-center px-4 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="text-center px-4 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider w-40">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-50">
                  {paginatedRoles.map((role, idx) => (
                    <tr
                      key={role.role}
                      className={`transition-colors ${idx % 2 === 0
                          ? "bg-white hover:bg-blue-50/60"
                          : "bg-blue-50/20 hover:bg-blue-50/60"
                        }`}
                    >
                      {/* Role */}
                      <td className="px-4 py-2.5 align-middle text-center">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold whitespace-nowrap shadow-sm ${getRoleBadgeColor(
                            role.role,
                          )}`}
                        >
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/70 shadow-sm text-sm">
                            {getRoleIcon(role.role)}
                          </span>
                          <span>
                            {(() => {
                              const value = role.name || role.role;
                              if (!value) return "";
                              if (value.includes("_")) {
                                return value
                                  .replace(/_/g, " ")
                                  .toLowerCase()
                                  .replace(/\b[a-z]/g, (char) =>
                                    char.toUpperCase(),
                                  );
                              }
                              return value;
                            })()}
                          </span>
                        </span>
                      </td>

                      {/* Description */}
                      <td className="px-4 py-2.5 align-middle text-center">
                        <div className="text-sm text-gray-700">
                          {role.description || "—"}
                        </div>
                      </td>

                      {/* Permissions */}
                      <td className="px-4 py-2.5 align-middle text-center">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 ring-1 ring-blue-300">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                          {role.permissions?.length ?? 0} permission
                          {(role.permissions?.length ?? 0) === 1 ? "" : "s"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-2.5 align-middle">
                        <div className="flex items-center justify-center gap-1 flex-nowrap">
                          <IconButton
                            icon={<Eye size={16} />}
                            label="View Permissions"
                            colorClass="text-blue-600 hover:text-white hover:bg-gradient-to-br hover:from-blue-500 hover:to-indigo-600"
                            onClick={() => viewRolePermissions(role.role)}
                          />
                          <IconButton
                            icon={<Pencil size={16} />}
                            label="Edit Role"
                            colorClass="text-indigo-600 hover:text-white hover:bg-gradient-to-br hover:from-indigo-500 hover:to-purple-600"
                            onClick={() => openEditRole(role)}
                          />
                          <IconButton
                            icon={<Trash2 size={16} />}
                            label="Delete Role"
                            colorClass="text-red-600 hover:text-white hover:bg-gradient-to-br hover:from-red-500 hover:to-rose-600"
                            onClick={() => {
                              setRoleToDelete(role);
                              setShowDeleteRoleModal(true);
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredRoles.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-10 text-center">
                        <div className="text-5xl mb-3">🛡️</div>
                        <p className="text-gray-600 font-medium">
                          No roles found
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          Try changing your search or create a new role
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredRoles.length > 0 && (
              <Pagination
                currentPage={roleCurrentPage}
                totalPages={roleTotalPages}
                pageSize={roleItemsPerPage}
                totalItems={roleTotal}
                onPageChange={(page) => {
                  setRoleCurrentPage(page);
                  fetchRoles(page);
                }}
                onPageSizeChange={(size) => {
                  setRoleItemsPerPage(size);
                  setRoleCurrentPage(1);
                  fetchRoles(1, size);
                }}
              />
            )}
          </div>
        </>
      );
    }

    // For users or tenants view
    return (
      <>
        {/* Statistics Cards — Users view only */}
        {activeView === "users" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {stats.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{item.icon}</span>
                  <h2 className={`text-2xl font-bold ${item.color}`}>
                    {item.value}
                  </h2>
                </div>
                <p className="text-gray-600 mt-0.5 text-sm">{item.title}</p>
              </div>
            ))}
          </div>
        )}

        {/* Users View */}
        {activeView === "users" && (
          <>
            {/* Actions Bar */}
            <div className="flex justify-between items-center flex-wrap gap-3 mb-4">
              <div className="flex gap-3 flex-wrap">
                <div ref={statusDropdownRef} className="relative inline-block">
                  <button
                    type="button"
                    onClick={() => setStatusDropdownOpen((p) => !p)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-xl text-sm font-medium transition-all duration-200 ${statusDropdownOpen
                        ? "border-blue-500 ring-2 ring-blue-100 shadow-sm"
                        : "border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-400 text-base">⚡</span>
                      <span className="truncate text-gray-700">
                        {statusFilter === "all"
                          ? "All Status"
                          : statusFilter === "active"
                            ? "Active Users"
                            : statusFilter === "deactivated"
                              ? "Deactivated Users"
                              : statusFilter === "pending"
                                ? "Pending Users"
                                : statusFilter}
                      </span>
                    </div>
                    <span
                      className={`text-gray-400 transition-transform duration-200 ${statusDropdownOpen ? "rotate-180" : ""
                        }`}
                    >
                      ▾
                    </span>
                  </button>

                  {statusDropdownOpen && (
                    <div className="absolute z-50 mt-2 left-0 min-w-full w-max bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                      <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        {[
                          { value: "all", label: "All Status" },
                          { value: "active", label: "Active Users" },
                          { value: "deactivated", label: "Deactivated Users" },
                          { value: "pending", label: "Pending Users" },
                        ].map(({ value, label }) => {
                          const isSelected = statusFilter === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              onClick={() => {
                                setStatusFilter(value);
                                setStatusDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between gap-8 px-3 py-2.5 rounded-lg text-sm text-left transition-colors whitespace-nowrap ${isSelected
                                  ? "bg-blue-50 text-blue-700 font-semibold"
                                  : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                              <span>{label}</span>
                              {isSelected && (
                                <span className="text-blue-600 shrink-0">
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div ref={roleDropdownRef} className="relative inline-block">
                  <button
                    type="button"
                    onClick={() => setRoleDropdownOpen((p) => !p)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-xl text-sm font-medium transition-all duration-200 ${roleDropdownOpen
                        ? "border-blue-500 ring-2 ring-blue-100 shadow-sm"
                        : "border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-400 text-base">👤</span>
                      <span className="truncate text-gray-700">
                        {roleFilter === "all"
                          ? "All Roles"
                          : (() => {
                            const r = allFilterRoles.find(
                              (r) => (r.role || r.name) === roleFilter,
                            );
                            const v = r?.role || r?.name || roleFilter;
                            return v
                              .replace(/_/g, " ")
                              .toLowerCase()
                              .replace(/\b[a-z]/g, (c) => c.toUpperCase());
                          })()}
                      </span>
                    </div>
                    <span
                      className={`text-gray-400 transition-transform duration-200 ${roleDropdownOpen ? "rotate-180" : ""}`}
                    >
                      ▾
                    </span>
                  </button>

                  {roleDropdownOpen && (
                    <div className="absolute z-50 mt-2 left-0 min-w-full w-max bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                      <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        {[
                          { role: "all", label: "All Roles" },
                          ...allFilterRoles.map((r) => ({
                            role: r.role || r.name,
                            label: (r.role || r.name || "")
                              .replace(/_/g, " ")
                              .toLowerCase()
                              .replace(/\b[a-z]/g, (c) => c.toUpperCase()),
                          })),
                        ].map(({ role, label }) => {
                          if (!role) return null;
                          const isSelected = roleFilter === role;
                          return (
                            <button
                              key={role}
                              type="button"
                              onClick={() => {
                                setRoleFilter(role);
                                setRoleDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between gap-8 px-3 py-2.5 rounded-lg text-sm text-left transition-colors whitespace-nowrap ${isSelected
                                  ? "bg-blue-50 text-blue-700 font-semibold"
                                  : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                              <span>{label}</span>
                              {isSelected && (
                                <span className="text-blue-600 shrink-0">
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div ref={tenantDropdownRef} className="relative inline-block">
                  <button
                    type="button"
                    onClick={() => setTenantDropdownOpen((prev) => !prev)}
                    className={`w-48 flex items-center justify-between gap-3 px-4 py-2.5
      bg-white border rounded-xl text-sm font-medium
      transition-all duration-200
      ${tenantDropdownOpen
                        ? "border-blue-500 ring-2 ring-blue-100 shadow-sm"
                        : "border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-400 text-base">🏢</span>

                      <span className="truncate text-gray-700">
                        {tenantFilter === "all"
                          ? "All Tenants"
                          : allFilterTenants.find(
                            (tenant) => tenant.id === tenantFilter,
                          )?.name || "All Tenants"}
                      </span>
                    </div>

                    <span
                      className={`text-gray-400 transition-transform duration-200 ${tenantDropdownOpen ? "rotate-180" : ""
                        }`}
                    >
                      ▾
                    </span>
                  </button>

                  {tenantDropdownOpen && (
                    <div className="absolute z-50 mt-2 left-0 w-48 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                      <div
                        className="max-h-72 overflow-y-auto p-1.5
          [scrollbar-width:none]
          [-ms-overflow-style:none]
          [&::-webkit-scrollbar]:hidden"
                      >
                        {/* All Tenants */}
                        <button
                          type="button"
                          onClick={() => {
                            setTenantFilter("all");
                            setTenantDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between gap-2
            px-3 py-2.5 rounded-lg text-sm text-left
            transition-colors
            ${tenantFilter === "all"
                              ? "bg-blue-50 text-blue-700 font-semibold"
                              : "text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                          <span>All Tenants</span>

                          {tenantFilter === "all" && (
                            <span className="text-blue-600 shrink-0">✓</span>
                          )}
                        </button>

                        {allFilterTenants.map((tenant) => {
                          const isSelected = tenantFilter === tenant.id;

                          return (
                            <button
                              key={tenant.id}
                              type="button"
                              onClick={() => {
                                setTenantFilter(tenant.id);
                                setTenantDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between gap-2
                px-3 py-2.5 rounded-lg text-sm text-left
                transition-colors
                ${isSelected
                                  ? "bg-blue-50 text-blue-700 font-semibold"
                                  : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                              <span className="truncate">{tenant.name}</span>

                              {isSelected && (
                                <span className="text-blue-600 shrink-0">
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })}

                        {allFilterTenants.length === 0 && (
                          <div className="px-3 py-3 text-sm text-gray-500 text-center">
                            No tenants available
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, email..."
                    className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64 transition"
                  />
                </div>

                <button
                  onClick={async () => {
                    const logs: any = await superAdminApi.getAuditLogs();
                    setIsUserAudit(false);
                    setSelectedAuditUser(null);
                    setAuditLogs(
                      Array.isArray(logs) ? logs : (logs.data ?? []),
                    );
                    setShowAuditModal(true);
                  }}
                  className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
                    bg-gradient-to-r from-slate-700 to-slate-800 text-white
                    border border-slate-700 shadow-sm
                    hover:from-slate-800 hover:to-slate-900 hover:shadow-md
                    active:scale-[0.98]
                    transition-all duration-200"
                >
                  <ClipboardList
                    size={16}
                    className="text-slate-300 group-hover:text-white transition-colors"
                  />
                  <span>Show Audit Logs</span>
                </button>

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition flex items-center gap-2"
                  >
                    ✕ Clear Filters
                  </button>
                )}
              </div>
            </div>

            {loading && (
              <div className="mb-4 text-center text-gray-500">
                Loading users...
              </div>
            )}

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm shadow-sm">
                    👥
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">
                      Users Management
                    </h2>
                    <p className="text-xs text-blue-700/70">
                      Manage all tenant users, administrators, and platform
                      access.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setTenantModalStep("choose");
                    setShowCreateTenantModal(true);
                  }}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition shadow-sm"
                >
                  + Create Users & Tenants
                </button>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-50/60 to-indigo-50/60 border-b border-blue-100">
                      <th className="text-center px-3 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider w-64">
                        User
                      </th>
                      <th className="text-center px-3 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider w-64">
                        Tenant
                      </th>
                      <th className="text-center px-3 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider w-48">
                        Role & Access
                      </th>
                      <th className="text-center px-3 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider w-32">
                        Department
                      </th>
                      <th className="text-center px-3 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider w-28">
                        Status
                      </th>
                      <th className="text-center px-3 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider w-24">
                        Last Login
                      </th>
                      <th className="text-center px-3 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider w-32">
                        Created By
                      </th>
                      <th className="text-center px-3 py-2.5 text-[11px] font-bold text-blue-700 uppercase tracking-wider w-40">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-50">
                    {paginatedUsers.map((user, idx) => (
                      <tr
                        key={user.id}
                        className={`transition-colors ${idx % 2 === 0
                            ? "bg-white hover:bg-blue-50/60"
                            : "bg-blue-50/20 hover:bg-blue-50/60"
                          }`}
                      >
                        {/* User */}
                        <td className="px-3 py-2.5 align-middle text-center">
                          <div className="flex flex-col items-center justify-center min-w-0">
                            <div className="font-semibold text-gray-900 truncate max-w-full">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5 justify-center">
                              <span className="text-xs text-gray-500 truncate">
                                {user.email}
                              </span>
                              {user.phone_number && (
                                <div className="relative group shrink-0">
                                  <Phone size={12} className="text-gray-400" />
                                  <span
                                    className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2
                                           whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white
                                           opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100
                                           transition-all duration-150 z-10"
                                  >
                                    {user.phone_number}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Tenant */}
                        <td className="px-3 py-2.5 align-middle text-center">
                          <div
                            className="text-sm font-medium text-gray-800 truncate max-w-[9rem] mx-auto"
                            title={user.tenant_name || "—"}
                          >
                            {user.tenant_name || "—"}
                          </div>
                          {user.tenant_email && (
                            <div
                              className="text-xs text-gray-500 truncate max-w-[9rem] mx-auto"
                              title={`Tenant email: ${user.tenant_email}`}
                            >
                              {user.tenant_email}
                            </div>
                          )}
                        </td>

                        {/* Role */}
                        <td className="px-3 py-2.5 align-middle text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-lg">
                              {getRoleIcon(user.role)}
                            </span>
                            <span
                              className={`px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm ${getRoleBadgeColor(user.role)}`}
                            >
                              {user.role === "SUPER_ADMIN"
                                ? "Super Admin"
                                : user.role === "CUSTOMER_ADMIN"
                                  ? "Administrator"
                                  : user.role === "CUSTOMER_MANAGER"
                                    ? "Manager"
                                    : user.role === "CUSTOMER_VIEWER"
                                      ? "Viewer"
                                      : user.role}
                            </span>
                          </div>
                        </td>

                        {/* Department */}
                        <td className="px-3 py-2.5 align-middle text-center">
                          <div className="text-sm text-gray-700">
                            {user.department || "—"}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-2.5 align-middle text-center">
                          {user.status.toLowerCase() === "active" ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full capitalize shadow-sm bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 ring-1 ring-emerald-300">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                              Active
                            </span>
                          ) : user.status.toLowerCase() === "pending" ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full capitalize shadow-sm bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 ring-1 ring-amber-300">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                              Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full capitalize shadow-sm bg-gradient-to-r from-rose-100 to-red-100 text-red-700 ring-1 ring-red-300">
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                              Deactivated
                            </span>
                          )}
                        </td>

                        {/* Last Login */}
                        <td className="px-3 py-2.5 align-middle text-center">
                          {user.last_login ? (
                            <div className="leading-tight">
                              <div className="text-xs font-medium text-gray-700">
                                {new Date(user.last_login).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-gray-400">
                                {new Date(user.last_login).toLocaleTimeString(
                                  [],
                                  { hour: "2-digit", minute: "2-digit" },
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>

                        {/* Created By */}
                        <td className="px-3 py-2.5 align-middle text-center">
                          {user.created_by ? (
                            <div className="text-sm text-gray-700">
                              {user.created_by.firstName}{" "}
                              {user.created_by.lastName}
                            </div>
                          ) : (
                            <span className="text-gray-400">System</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-3 py-2.5 align-middle">
                          <div className="flex items-center justify-center gap-1 flex-nowrap">
                            <IconButton
                              icon={<Pencil size={16} />}
                              label="Edit"
                              colorClass="text-blue-600 hover:text-white hover:bg-gradient-to-br hover:from-blue-500 hover:to-indigo-600"
                              onClick={() => {
                                setEditingUser(user);
                                setEditForm({
                                  firstName: user.first_name,
                                  lastName: user.last_name,
                                  role: user.role,
                                  department: user.department || "",
                                  phoneNumber: user.phone_number || "",
                                  status: user.status,
                                });
                                setShowEditModal(true);
                              }}
                            />

                            <IconButton
                              icon={<ClipboardList size={16} />}
                              label="Audit Logs"
                              colorClass="text-purple-600 hover:text-white hover:bg-gradient-to-br hover:from-purple-500 hover:to-fuchsia-600"
                              onClick={() => openAuditLogs(user)}
                            />

                            <IconButton
                              icon={<Trash2 size={16} />}
                              label="Delete"
                              colorClass="text-red-600 hover:text-white hover:bg-gradient-to-br hover:from-red-500 hover:to-rose-600"
                              onClick={() => {
                                setUserToDelete(user);
                                setShowDeleteModal(true);
                              }}
                            />

                            {user.status.toLowerCase() === "active" ? (
                              <IconButton
                                icon={<Power size={16} />}
                                label="Deactivate"
                                colorClass="text-orange-600 hover:text-white hover:bg-gradient-to-br hover:from-orange-500 hover:to-amber-600"
                                onClick={async () => {
                                  try {
                                    await superAdminApi.deactivateUser(user.id);
                                    toast.success(
                                      "User deactivated successfully.",
                                    );
                                    await fetchUsers(currentUserPage);
                                    await fetchUserStats();
                                  } catch (err) {
                                    toast.error(
                                      err instanceof Error
                                        ? err.message
                                        : "Failed to deactivate user",
                                    );
                                  }
                                }}
                              />
                            ) : (
                              <IconButton
                                icon={<RotateCcw size={16} />}
                                label="Reactivate"
                                colorClass="text-emerald-600 hover:text-white hover:bg-gradient-to-br hover:from-emerald-500 hover:to-green-600"
                                onClick={async () => {
                                  try {
                                    await superAdminApi.reactivateUser(user.id);
                                    toast.success(
                                      "User reactivated successfully.",
                                    );
                                    await fetchUsers(currentUserPage);
                                    await fetchUserStats();
                                  } catch (err) {
                                    toast.error(
                                      err instanceof Error
                                        ? err.message
                                        : "Failed to reactivate user",
                                    );
                                  }
                                }}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && !loading && (
                      <tr>
                        <td colSpan={8} className="px-6 py-10 text-center">
                          <div className="text-5xl mb-3">👥</div>
                          <p className="text-gray-600 font-medium">
                            No users found
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            Try changing your filters or create a new user
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentUserPage}
                totalPages={userTotalPages}
                pageSize={usersPerPage}
                totalItems={userTotal}
                onPageChange={(page) => {
                  setCurrentUserPage(page);
                  fetchUsers(page);
                }}
                onPageSizeChange={(size) => {
                  setUsersPerPage(size);
                  setCurrentUserPage(1);
                  fetchUsers(1, size);
                }}
              />
            </div>
          </>
        )}

        {/* Tenants View */}
        {activeView === "tenants" && (
          <div className="space-y-4">
            {/* Tenant Filters */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative w-72">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    placeholder="Search Tenant..."
                    value={tenantSearchTerm}
                    onChange={(e) => setTenantSearchTerm(e.target.value)}
                    className="pl-9 pr-10 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full transition"
                  />
                  {tenantSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setTenantSearchTerm("")}
                      aria-label="Clear search"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div
                  ref={tenantStatusDropdownRef}
                  className="relative inline-block"
                >
                  <button
                    type="button"
                    onClick={() => setTenantStatusDropdownOpen((p) => !p)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-xl text-sm font-medium transition-all duration-200 ${tenantStatusDropdownOpen
                        ? "border-blue-500 ring-2 ring-blue-100 shadow-sm"
                        : "border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-400 text-base">⚡</span>
                      <span className="truncate text-gray-700">
                        {tenantStatusFilter === "all"
                          ? "All Status"
                          : tenantStatusFilter === "active"
                            ? "Active"
                            : tenantStatusFilter === "inactive"
                              ? "Inactive"
                              : tenantStatusFilter === "suspended"
                                ? "Suspended"
                                : tenantStatusFilter}
                      </span>
                    </div>
                    <span
                      className={`text-gray-400 transition-transform duration-200 ${tenantStatusDropdownOpen ? "rotate-180" : ""
                        }`}
                    >
                      ▾
                    </span>
                  </button>

                  {tenantStatusDropdownOpen && (
                    <div className="absolute z-50 mt-2 left-0 min-w-full w-max bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                      <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        {[
                          { value: "all", label: "All Status" },
                          { value: "active", label: "Active" },
                          { value: "inactive", label: "Inactive" },
                          { value: "suspended", label: "Suspended" },
                        ].map(({ value, label }) => {
                          const isSelected = tenantStatusFilter === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              onClick={() => {
                                setTenantStatusFilter(value);
                                setTenantStatusDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between gap-8 px-3 py-2.5 rounded-lg text-sm text-left transition-colors whitespace-nowrap ${isSelected
                                  ? "bg-blue-50 text-blue-700 font-semibold"
                                  : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                              <span>{label}</span>
                              {isSelected && (
                                <span className="text-blue-600 shrink-0">
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {hasActiveTenantFilters && (
                  <button
                    onClick={clearTenantFilters}
                    className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition flex items-center gap-2"
                  >
                    ✕ Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Tenant Table */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm shadow-sm">
                    🏢
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">
                      Tenant Management
                    </h2>
                    <p className="text-xs text-blue-700/70">
                      Manage organizations registered in the platform.
                    </p>
                  </div>
                </div>
                <span className="text-xs font-semibold text-blue-700 bg-white px-2.5 py-1 rounded-full border border-blue-200 shadow-sm">
                  {tenantTotal} total
                </span>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-50/60 to-indigo-50/60 border-b border-blue-100">
                      <th className="px-3 py-2.5 text-center text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-3 py-2.5 text-center text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                        Subdomain
                      </th>
                      <th className="px-3 py-2.5 text-center text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-3 py-2.5 text-center text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 py-2.5 text-center text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-blue-50">
                    {tenantLoading ? (
                      <tr>
                        <td colSpan={5} className="py-10 text-center">
                          <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          <p className="text-sm text-gray-500 mt-2">
                            Loading tenants...
                          </p>
                        </td>
                      </tr>
                    ) : filteredTenants.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-10 text-center">
                          <div className="text-5xl mb-3">🏢</div>
                          <p className="text-gray-600 font-medium">
                            No tenants found
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            Try changing your filters
                          </p>
                        </td>
                      </tr>
                    ) : (
                      paginatedTenants.map((tenant, idx) => {
                        return (
                          <tr
                            key={tenant.id}
                            className={`transition-colors ${idx % 2 === 0
                                ? "bg-white hover:bg-blue-50/60"
                                : "bg-blue-50/20 hover:bg-blue-50/60"
                              }`}
                          >
                            {/* Company */}
                            <td className="px-3 py-2.5 text-center">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                {tenant.name}
                              </div>
                            </td>

                            {/* Subdomain */}
                            <td className="px-3 py-2.5 text-center">
                              <span className="inline-flex items-center gap-1 text-xs text-indigo-700 font-mono font-medium bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-200">
                                {tenant.subdomain}
                              </span>
                            </td>

                            {/* Email */}
                            <td className="px-3 py-2.5 text-center">
                              <div
                                className="text-sm text-gray-700 truncate"
                                title={tenant.email}
                              >
                                {tenant.email}
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-3 py-2.5 text-center">
                              <span
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize shadow-sm ${tenant.status === "active"
                                    ? "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 ring-1 ring-emerald-300"
                                    : tenant.status === "suspended"
                                      ? "bg-gradient-to-r from-amber-100 to-orange-100 text-orange-700 ring-1 ring-orange-300"
                                      : "bg-gradient-to-r from-rose-100 to-red-100 text-red-700 ring-1 ring-red-300"
                                  }`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${tenant.status === "active"
                                      ? "bg-emerald-500 animate-pulse"
                                      : tenant.status === "suspended"
                                        ? "bg-orange-500"
                                        : "bg-red-500"
                                    }`}
                                />
                                {tenant.status}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="px-3 py-2.5 text-center">
                              <div className="inline-flex items-center justify-center gap-1">
                                <IconButton
                                  icon={<Pencil size={16} />}
                                  label="Edit"
                                  colorClass="text-blue-600 hover:text-white hover:bg-gradient-to-br hover:from-blue-500 hover:to-indigo-600"
                                  onClick={async () => {
                                    setEditingTenant(tenant);
                                    setTenantEditForm({
                                      name: tenant.name || "",
                                      subdomain: tenant.subdomain || "",
                                      email: tenant.email || "",
                                      phoneNumber:
                                        (tenant as any).phone_number || "",
                                      status: tenant.status || "",
                                      maxUsers: String(tenant.max_users ?? ""),
                                      logoUrl: (tenant as any).logo_url || "",
                                      primaryColor: "#2563EB",
                                      secondaryColor: "#F97316",
                                      themeMode: "CUSTOM",
                                      storageLimitValue: String(
                                        (tenant as any).storage_limit_value ??
                                        "",
                                      ),
                                      storageLimitUnit:
                                        (tenant as any).storage_limit_unit ||
                                        "GB",
                                    });
                                    setShowEditTenantModal(true);

                                    const theme = await fetchTenantTheme(
                                      tenant.id,
                                    );
                                    if (theme) {
                                      setTenantEditForm((prev) => ({
                                        ...prev,
                                        primaryColor: theme.primaryColor,
                                        secondaryColor: theme.secondaryColor,
                                        themeMode: theme.themeMode,
                                      }));
                                    }
                                  }}
                                />

                                <IconButton
                                  icon={<ClipboardList size={16} />}
                                  label="Manage T&C"
                                  colorClass="text-purple-600 hover:text-white hover:bg-gradient-to-br hover:from-purple-500 hover:to-indigo-600"
                                  onClick={() => {
                                    navigate(
                                      `/super-admin/tenants/${tenant.id}/tnc`,
                                    );
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <Pagination
              currentPage={currentTenantPage}
              totalPages={tenantTotalPages}
              pageSize={tenantsPerPage}
              totalItems={tenantTotal}
              onPageChange={(page) => {
                setCurrentTenantPage(page);
                fetchTenants(page);
              }}
              onPageSizeChange={(size) => {
                setTenantsPerPage(size);
                setCurrentTenantPage(1);
                fetchTenants(1, size);
              }}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <div className="px-6 pt-1 pb-6">
        {/* Page Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {activeView === "roles"
              ? "Roles & Permissions Management"
              : activeView === "tenants"
                ? "Tenant Management"
                : "Users Management"}
          </h1>
          <p className="text-gray-500 mt-1">
            {activeView === "roles"
              ? "Manage roles and the permissions assigned to each role."
              : activeView === "tenants"
                ? "Manage organizations registered in the platform."
                : "Manage all tenant users, administrators, and platform access."}
          </p>
        </div>

        {renderContent()}

        {/* All Modals - Keep all existing modals here */}
        {/* Audit Logs Modal */}
        {showAuditModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-[92vw] max-w-6xl h-[85vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b">
                <div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">
                      {isUserAudit && selectedAuditUser
                        ? `Audit Logs: ${selectedAuditUser.first_name} ${selectedAuditUser.last_name}`
                        : "Audit Logs"}
                    </h2>

                    <p className="text-gray-500 mt-1">
                      {isUserAudit && selectedAuditUser
                        ? `${selectedAuditUser.email} | ${selectedAuditUser.role.toLowerCase()}`
                        : "Activity history of users and administrators"}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setShowAuditModal(false)}
                  className="w-10 h-10 rounded-full hover:bg-gray-100 text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {auditLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="text-7xl mb-6">📋</div>
                    <h3 className="text-2xl font-semibold text-gray-700">
                      No audit logs found
                    </h3>
                    <p className="text-gray-500 mt-3">
                      {isUserAudit
                        ? "No audit logs found for this user."
                        : "There are currently no audit logs available."}
                    </p>
                  </div>
                ) : (
                  auditLogs.map((log) => (
                    <div
                      key={log.id}
                      className="border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-200 p-6"
                    >
                      <div className="flex justify-between gap-8">
                        <div className="flex-1">
                          <div className="flex items-center gap-4">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${log.action.toLowerCase().includes("login")
                                  ? "bg-green-100 text-green-700"
                                  : log.action.toLowerCase().includes("logout")
                                    ? "bg-gray-100 text-gray-700"
                                    : log.action
                                      .toLowerCase()
                                      .includes("create")
                                      ? "bg-blue-100 text-blue-700"
                                      : log.action
                                        .toLowerCase()
                                        .includes("update")
                                        ? "bg-yellow-100 text-yellow-700"
                                        : log.action
                                          .toLowerCase()
                                          .includes("delete")
                                          ? "bg-red-100 text-red-700"
                                          : log.action
                                            .toLowerCase()
                                            .includes("deactivate")
                                            ? "bg-orange-100 text-orange-700"
                                            : "bg-purple-100 text-purple-700"
                                }`}
                            >
                              {log.action.toUpperCase()}
                            </span>
                            <h3 className="text-xl font-semibold text-gray-900">
                              {log.user_email}
                            </h3>
                          </div>
                          <p className="mt-4 text-gray-700">{log.details}</p>
                          <div className="grid grid-cols-2 gap-y-2 gap-x-8 mt-5 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">
                                Performed By
                              </span>
                              <p className="text-gray-500">
                                {log.performed_by_email}
                              </p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">
                                Role
                              </span>
                              <p className="text-gray-500">
                                {log.performed_by_role}
                              </p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">
                                Location
                              </span>
                              <p className="text-gray-500">
                                {log.location_city}, {log.location_country}
                              </p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">
                                IP Address
                              </span>
                              <p className="text-gray-500">{log.ip_address}</p>
                            </div>
                            {log.user_agent && (
                              <div className="col-span-2">
                                <span className="font-medium text-gray-700">
                                  Device
                                </span>
                                <p className="text-gray-500 break-all">
                                  {log.user_agent}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right min-w-[140px] border-l pl-6">
                          <div className="text-gray-400 text-sm">Date</div>
                          <div className="font-semibold">
                            {new Date(log.timestamp).toLocaleDateString()}
                          </div>
                          <div className="mt-2 text-gray-400 text-sm">Time</div>
                          <div className="font-semibold">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Create Tenant Modal - Choose Step */}
        {showCreateTenantModal && tenantModalStep === "choose" && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg shadow-md">
                    ⚙️
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Tenant Setup
                    </h2>
                    <p className="text-sm text-blue-700/70 mt-0.5">
                      Choose how you'd like to proceed.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowCreateTenantModal(false);
                    setTenantModalStep("choose");
                  }}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-white hover:text-gray-800 hover:shadow-sm transition text-lg"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              {/* Options */}
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Create New Tenant */}
                  <button
                    onClick={() => {
                      setTenantForm({
                        tenant: {
                          name: "",
                          subdomain: "",
                          email: "",
                          phoneNumber: "",
                          maxUsers: "",
                          logoUrl: "",
                          primaryColor: "#2563EB",
                          secondaryColor: "#F97316",
                          themeMode: "CUSTOM",
                          storageLimitValue: "",
                          storageLimitUnit: "GB",
                        },
                        user: {
                          firstName: "",
                          lastName: "",
                          email: "",
                          role: "CUSTOMER_ADMIN",
                          department: "",
                          phoneNumber: "",
                          password: "",
                          confirmPassword: "",
                        },
                      });
                      setShowPassword(false);
                      setShowConfirmPassword(false);
                      setLogoFile(null);
                      setLogoPreviewUrl("");
                      setTenantModalStep("create");
                    }}
                    className="group relative text-left p-6 rounded-2xl border-2 border-gray-200 bg-white hover:border-blue-400 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-3xl mb-4 group-hover:scale-110 group-hover:from-blue-500 group-hover:to-indigo-600 transition-all duration-300">
                      <span className="group-hover:brightness-0 group-hover:invert transition">
                        ➕
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 group-hover:text-blue-700 transition">
                      Create New Tenant
                    </h3>
                    <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                      Create a brand new company with its own administrator
                      account.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 group-hover:text-blue-700 transition">
                      Get started
                      <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">
                        →
                      </span>
                    </div>
                  </button>

                  {/* Select Existing Tenant */}
                  <button
                    onClick={() => {
                      resetExistingForm();
                      setTenantModalStep("existing");
                    }}
                    className="group relative text-left p-6 rounded-2xl border-2 border-gray-200 bg-white hover:border-emerald-400 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center text-3xl mb-4 group-hover:scale-110 group-hover:from-emerald-500 group-hover:to-green-600 transition-all duration-300">
                      <span className="group-hover:brightness-0 group-hover:invert transition">
                        🏢
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 group-hover:text-emerald-700 transition">
                      Select Existing Tenant
                    </h3>
                    <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                      Add users or administrators to an existing tenant.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 group-hover:text-emerald-700 transition">
                      Continue
                      <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">
                        →
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Tenant Modal - Create Step */}
        {showCreateTenantModal && tenantModalStep === "create" && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg shadow-md">
                    🏢
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Create Tenant
                    </h2>
                    <p className="text-sm text-blue-700/70 mt-0.5">
                      Create a new organization and its administrator account.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setTenantModalStep("choose")}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-white hover:text-gray-800 hover:shadow-sm transition text-lg"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {/* Company Information */}
                <Section
                  icon="🏢"
                  iconBg="bg-blue-50"
                  iconText="text-blue-600"
                  title="Company Information"
                >
                  {[
                    { ph: "Company Name *", key: "name" },
                    { ph: "Subdomain (optional)", key: "subdomain" },
                    {
                      ph: "Company Email *",
                      key: "email",
                      type: "email",
                      autoComplete: "off",
                    },
                    {
                      ph: "Company Phone",
                      key: "phoneNumber",
                      type: "tel",
                      inputMode: "numeric",
                      autoComplete: "off",
                    },
                  ].map((f) => (
                    <input
                      key={f.key}
                      type={f.type}
                      inputMode={f.inputMode as any}
                      autoComplete={f.autoComplete}
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      placeholder={f.ph}
                      value={
                        tenantForm.tenant[
                        f.key as keyof typeof tenantForm.tenant
                        ] as string
                      }
                      onChange={(e) =>
                        setTenantForm({
                          ...tenantForm,
                          tenant: {
                            ...tenantForm.tenant,
                            [f.key]: e.target.value,
                          },
                        })
                      }
                    />
                  ))}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      User Limit
                    </label>
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 10"
                      value={tenantForm.tenant.maxUsers}
                      onChange={(e) =>
                        setTenantForm({
                          ...tenantForm,
                          tenant: {
                            ...tenantForm.tenant,
                            maxUsers: e.target.value,
                          },
                        })
                      }
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Storage Limit
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        placeholder="e.g. 50"
                        value={tenantForm.tenant.storageLimitValue}
                        onChange={(e) =>
                          setTenantForm({
                            ...tenantForm,
                            tenant: {
                              ...tenantForm.tenant,
                              storageLimitValue: e.target.value,
                            },
                          })
                        }
                        className="flex-1 min-w-0 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      />
                      <select
                        value={tenantForm.tenant.storageLimitUnit}
                        onChange={(e) =>
                          setTenantForm({
                            ...tenantForm,
                            tenant: {
                              ...tenantForm.tenant,
                              storageLimitUnit: e.target.value,
                            },
                          })
                        }
                        className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="MB">MB</option>
                        <option value="GB">GB</option>
                        <option value="TB">TB</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Company Logo
                    </label>
                    <div className="flex items-center gap-3">
                      {logoPreviewUrl && (
                        <img
                          src={logoPreviewUrl}
                          alt="Logo"
                          className="w-11 h-11 rounded-lg object-cover border border-gray-200 shrink-0"
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoSelect}
                        disabled={uploadingLogo}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:text-xs file:font-medium"
                      />
                    </div>
                    {uploadingLogo && (
                      <p className="text-xs text-blue-600 mt-1">
                        Uploading logo...
                      </p>
                    )}
                  </div>
                </Section>

                {/* Website Theme */}
                <Section
                  icon="🎨"
                  iconBg="bg-indigo-50"
                  iconText="text-indigo-600"
                  title="Website Theme"
                  subtitle="This is the color scheme the tenant's users will see after logging in."
                >
                  {(["primaryColor", "secondaryColor"] as const).map((key) => (
                    <div key={key}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        {key === "primaryColor"
                          ? "Primary Color"
                          : "Secondary Color"}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={tenantForm.tenant[key]}
                          onChange={(e) =>
                            setTenantForm({
                              ...tenantForm,
                              tenant: {
                                ...tenantForm.tenant,
                                [key]: e.target.value,
                              },
                            })
                          }
                          className="w-11 h-11 rounded-lg border border-gray-300 cursor-pointer p-0 shrink-0"
                        />
                        <input
                          type="text"
                          value={tenantForm.tenant[key]}
                          onChange={(e) =>
                            setTenantForm({
                              ...tenantForm,
                              tenant: {
                                ...tenantForm.tenant,
                                [key]: e.target.value,
                              },
                            })
                          }
                          className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition font-mono"
                        />
                      </div>
                    </div>
                  ))}
                  <div className="sm:col-span-2 flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
                    <span className="text-xs font-medium text-gray-500">
                      Preview:
                    </span>
                    {(["primaryColor", "secondaryColor"] as const).map((k) => (
                      <span
                        key={k}
                        className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: tenantForm.tenant[k] }}
                      />
                    ))}
                  </div>
                </Section>

                {/* Customer Administrator */}
                <Section
                  icon="👑"
                  iconBg="bg-purple-50"
                  iconText="text-purple-600"
                  title="Customer Administrator"
                >
                  <input
                    type="text"
                    name="fakeUsername"
                    autoComplete="username"
                    className="hidden"
                  />
                  <input
                    type="password"
                    name="fakePassword"
                    autoComplete="current-password"
                    className="hidden"
                  />
                  {[
                    { ph: "First Name *", key: "firstName" },
                    { ph: "Last Name *", key: "lastName" },
                    {
                      ph: "Email *",
                      key: "email",
                      type: "email",
                      autoComplete: "off",
                    },
                    { ph: "Department", key: "department" },
                    {
                      ph: "Phone Number",
                      key: "phoneNumber",
                      type: "tel",
                      inputMode: "numeric",
                      autoComplete: "off",
                    },
                  ].map((f) => (
                    <input
                      key={f.key}
                      type={f.type}
                      inputMode={f.inputMode as any}
                      autoComplete={f.autoComplete}
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      placeholder={f.ph}
                      value={
                        tenantForm.user[
                        f.key as keyof typeof tenantForm.user
                        ] as string
                      }
                      onChange={(e) =>
                        setTenantForm({
                          ...tenantForm,
                          user: { ...tenantForm.user, [f.key]: e.target.value },
                        })
                      }
                    />
                  ))}
                  <div className="sm:col-span-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                    <span className="text-blue-500 text-base leading-none mt-0.5">
                      ℹ️
                    </span>
                    <p className="text-xs text-blue-700 leading-relaxed">
                      A temporary password will be generated automatically and
                      sent to the administrator's email address. They'll be
                      prompted to set a new password on first login.
                    </p>
                  </div>
                </Section>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                <button
                  onClick={() => setTenantModalStep("choose")}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-xl hover:bg-white transition"
                >
                  ← Back
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowCreateTenantModal(false);
                      setTenantModalStep("choose");
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-xl hover:bg-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createTenant}
                    disabled={creatingTenant}
                    className={`px-5 py-2 text-sm font-semibold rounded-xl text-white shadow-sm transition ${creatingTenant
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                      }`}
                  >
                    {creatingTenant ? "Creating..." : "Create Tenant"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Tenant Modal - Existing Step */}
        {showCreateTenantModal && tenantModalStep === "existing" && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    Add User to Tenant
                  </h2>
                  <p className="text-gray-500 mt-1">
                    Select an existing tenant, choose a role, and fill in the
                    user's details.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Fields marked * are required.
                  </p>
                </div>
                <button
                  onClick={() => setTenantModalStep("choose")}
                  className="text-xl"
                >
                  ✕
                </button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Tenant *
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500"
                    value={existingForm.tenantId}
                    onChange={(e) =>
                      setExistingForm({
                        ...existingForm,
                        tenantId: e.target.value,
                      })
                    }
                    disabled={tenantOptionsLoading}
                  >
                    <option value="">
                      {tenantOptionsLoading
                        ? "Loading tenants…"
                        : "Select a tenant"}
                    </option>
                    {tenantOptions.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Role *
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500"
                    value={existingForm.role}
                    onChange={(e) =>
                      setExistingForm({ ...existingForm, role: e.target.value })
                    }
                  >
                    <option value="CUSTOMER_ADMIN">Customer Admin</option>
                    <option value="CUSTOMER_MANAGER">Customer Manager</option>
                    <option value="OPERATOR">Operator</option>
                    <option value="VIEWER">Viewer</option>
                    <option value="GUEST_USER">Guest User</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="fakeUsername"
                    autoComplete="username"
                    className="hidden"
                  />
                  <input
                    type="password"
                    name="fakePassword"
                    autoComplete="current-password"
                    className="hidden"
                  />
                  <input
                    className="w-full border border-gray-300 rounded-xl px-4 py-3"
                    placeholder="First Name *"
                    value={existingForm.firstName}
                    onChange={(e) =>
                      setExistingForm({
                        ...existingForm,
                        firstName: e.target.value,
                      })
                    }
                  />
                  <input
                    className="w-full border border-gray-300 rounded-xl px-4 py-3"
                    placeholder="Last Name *"
                    value={existingForm.lastName}
                    onChange={(e) =>
                      setExistingForm({
                        ...existingForm,
                        lastName: e.target.value,
                      })
                    }
                  />
                  <input
                    type="email"
                    name="existingEmail"
                    autoComplete="off"
                    className="w-full border border-gray-300 rounded-xl px-4 py-3"
                    placeholder="Email *"
                    value={existingForm.email}
                    onChange={(e) =>
                      setExistingForm({
                        ...existingForm,
                        email: e.target.value,
                      })
                    }
                  />
                  <input
                    className="w-full border border-gray-300 rounded-xl px-4 py-3"
                    placeholder="Department"
                    value={existingForm.department}
                    onChange={(e) =>
                      setExistingForm({
                        ...existingForm,
                        department: e.target.value,
                      })
                    }
                  />
                  <input
                    type="tel"
                    name="existingPhone"
                    autoComplete="off"
                    inputMode="numeric"
                    className="w-full border border-gray-300 rounded-xl px-4 py-3"
                    placeholder="Phone"
                    value={existingForm.phoneNumber}
                    onChange={(e) =>
                      setExistingForm({
                        ...existingForm,
                        phoneNumber: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setTenantModalStep("choose")}
                  className="px-5 py-2.5 border rounded-xl hover:bg-gray-50"
                >
                  ← Back
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowCreateTenantModal(false);
                      setTenantModalStep("choose");
                    }}
                    className="px-5 py-2.5 border rounded-xl hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createUserInExistingTenant}
                    disabled={existingSubmitting}
                    className={`px-6 py-2.5 rounded-xl text-white ${existingSubmitting
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700"
                      }`}
                  >
                    {existingSubmitting ? "Adding..." : "Add User"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && editingUser && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Edit User</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-2xl"
                >
                  ✕
                </button>
              </div>
              <div className="col-span-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <span className="text-blue-500 text-base leading-none mt-0.5">
                  ℹ️
                </span>
                <p className="text-xs text-blue-700 leading-relaxed">
                  A temporary password will be generated automatically and sent
                  to the user's email address. They'll be prompted to set a new
                  password on first login.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    className="w-full border rounded-lg px-4 py-2"
                    value={editForm.firstName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, firstName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    className="w-full border rounded-lg px-4 py-2"
                    value={editForm.lastName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, lastName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    className="w-full border rounded-lg px-4 py-2"
                    value={editForm.role}
                    onChange={(e) =>
                      setEditForm({ ...editForm, role: e.target.value })
                    }
                  >
                    <option value="SUPER_ADMIN">Super Admin</option>
                    <option value="CUSTOMER_ADMIN">Administrator</option>
                    <option value="CUSTOMER_MANAGER">Manager</option>
                    <option value="CUSTOMER_VIEWER">Viewer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department
                  </label>
                  <input
                    className="w-full border rounded-lg px-4 py-2"
                    value={editForm.department}
                    onChange={(e) =>
                      setEditForm({ ...editForm, department: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    className="w-full border rounded-lg px-4 py-2"
                    value={editForm.phoneNumber}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phoneNumber: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    className="w-full border rounded-lg px-4 py-2"
                    value={editForm.status}
                    onChange={(e) =>
                      setEditForm({ ...editForm, status: e.target.value })
                    }
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="DEACTIVATED">Deactivated</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-5 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={saveUser}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Tenant Modal */}
        {showEditTenantModal && editingTenant && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Edit Tenant</h2>
                <button
                  onClick={() => setShowEditTenantModal(false)}
                  className="text-2xl"
                >
                  ✕
                </button>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    className="w-full border rounded-lg px-4 py-2"
                    value={tenantEditForm.name}
                    onChange={(e) =>
                      setTenantEditForm({
                        ...tenantEditForm,
                        name: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subdomain
                  </label>
                  <input
                    className="w-full border rounded-lg px-4 py-2"
                    value={tenantEditForm.subdomain}
                    onChange={(e) =>
                      setTenantEditForm({
                        ...tenantEditForm,
                        subdomain: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full border rounded-lg px-4 py-2"
                    value={tenantEditForm.email}
                    onChange={(e) =>
                      setTenantEditForm({
                        ...tenantEditForm,
                        email: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    className="w-full border rounded-lg px-4 py-2"
                    value={tenantEditForm.phoneNumber}
                    onChange={(e) =>
                      setTenantEditForm({
                        ...tenantEditForm,
                        phoneNumber: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    className="w-full border rounded-lg px-4 py-2"
                    value={tenantEditForm.status}
                    onChange={(e) =>
                      setTenantEditForm({
                        ...tenantEditForm,
                        status: e.target.value,
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Limit
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="w-full border rounded-lg px-4 py-2"
                    value={tenantEditForm.maxUsers}
                    onChange={(e) =>
                      setTenantEditForm({
                        ...tenantEditForm,
                        maxUsers: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Storage Limit
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      step="any"
                      className="flex-1 min-w-0 border rounded-lg px-4 py-2"
                      value={tenantEditForm.storageLimitValue}
                      onChange={(e) =>
                        setTenantEditForm({
                          ...tenantEditForm,
                          storageLimitValue: e.target.value,
                        })
                      }
                    />
                    <select
                      className="border rounded-lg px-3 py-2 bg-white"
                      value={tenantEditForm.storageLimitUnit}
                      onChange={(e) =>
                        setTenantEditForm({
                          ...tenantEditForm,
                          storageLimitUnit: e.target.value,
                        })
                      }
                    >
                      <option value="MB">MB</option>
                      <option value="GB">GB</option>
                      <option value="TB">TB</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={tenantEditForm.primaryColor}
                      onChange={(e) =>
                        setTenantEditForm({
                          ...tenantEditForm,
                          primaryColor: e.target.value,
                        })
                      }
                      className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0"
                    />
                    <input
                      className="flex-1 border rounded-lg px-4 py-2"
                      value={tenantEditForm.primaryColor}
                      onChange={(e) =>
                        setTenantEditForm({
                          ...tenantEditForm,
                          primaryColor: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={tenantEditForm.secondaryColor}
                      onChange={(e) =>
                        setTenantEditForm({
                          ...tenantEditForm,
                          secondaryColor: e.target.value,
                        })
                      }
                      className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0"
                    />
                    <input
                      className="flex-1 border rounded-lg px-4 py-2"
                      value={tenantEditForm.secondaryColor}
                      onChange={(e) =>
                        setTenantEditForm({
                          ...tenantEditForm,
                          secondaryColor: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Logo
                  </label>
                  <div className="flex items-center gap-3">
                    {tenantEditForm.logoUrl && (
                      <div className="relative w-12 h-12 shrink-0">
                        <img
                          src={tenantEditForm.logoUrl}
                          alt="Current logo"
                          className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setTenantEditForm((prev) => ({
                              ...prev,
                              logoUrl: "",
                            }))
                          }
                          aria-label="Remove logo"
                          className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-800 text-white text-[10px] leading-none hover:bg-red-600 transition"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      onChange={handleEditLogoSelect}
                      disabled={uploadingLogo}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700"
                    />
                  </div>
                  {uploadingLogo && (
                    <p className="text-xs text-gray-500 mt-1">Uploading...</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowEditTenantModal(false)}
                  className="px-5 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTenant}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Modal */}
        {showDeleteModal && userToDelete && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-gray-900">Delete User</h2>
              <p className="mt-4 text-gray-600">Do you want to delete</p>
              <p className="font-semibold text-lg mt-2">
                "{userToDelete.first_name} {userToDelete.last_name}" ?
              </p>
              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  No
                </button>
                <button
                  onClick={confirmDeleteUser}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Role Modal */}
        {showCreateRoleModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-start mb-1">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    Create Role
                  </h2>
                  <p className="text-gray-500 mt-1">
                    Name the role and choose which permissions it grants.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Fields marked * are required.
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateRoleModal(false)}
                  className="text-xl"
                >
                  ✕
                </button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
                <h3 className="text-lg font-semibold mb-1">
                  Role Details <span className="text-red-500">*</span>
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    required
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 transition focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Role Name *"
                    value={roleForm.name}
                    onChange={(e) =>
                      setRoleForm({ ...roleForm, name: e.target.value })
                    }
                  />
                  <input
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="Description *"
                    value={roleForm.description}
                    onChange={(e) =>
                      setRoleForm({
                        ...roleForm,
                        description: e.target.value.replace(/\s+/g, " "),
                      })
                    }
                  />
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mt-4">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div>
                    <h3 className="text-lg font-semibold">
                      Permissions ({roleForm.permissions.length} selected){" "}
                      <span className="text-red-500">*</span>
                    </h3>
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleSelectAllPermissions}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium text-gray-600">
                        Select All
                      </span>
                    </label>
                  </div>
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                    <input
                      type="text"
                      value={permissionSearch}
                      onChange={(e) => setPermissionSearch(e.target.value)}
                      placeholder="Search permissions..."
                      className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64 transition"
                    />
                  </div>
                </div>

                <div
                  className={`rounded-xl overflow-hidden bg-white ${roleForm.permissions.length === 0 ? "border border-red-400" : "border border-gray-200"}`}
                >
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-12 px-4 py-3"></th>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                          Permission
                        </th>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {permissionsLoading && (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-4 py-8 text-center text-gray-400"
                          >
                            Loading permissions...
                          </td>
                        </tr>
                      )}
                      {!permissionsLoading &&
                        filteredPermissions.map((perm) => (
                          <tr
                            key={perm.permission_code}
                            onClick={() =>
                              togglePermission(perm.permission_code)
                            }
                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition"
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={roleForm.permissions.includes(
                                  perm.permission_code,
                                )}
                                onChange={() =>
                                  togglePermission(perm.permission_code)
                                }
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-800">
                              {perm.permission_code
                                ?.replace(/_/g, " ")
                                .toLowerCase()
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                              <span className="ml-2 text-xs text-gray-400">
                                {perm.module}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {perm.description}
                            </td>
                          </tr>
                        ))}
                      {!permissionsLoading &&
                        filteredPermissions.length === 0 && (
                          <tr>
                            <td
                              colSpan={3}
                              className="px-4 py-8 text-center text-gray-400"
                            >
                              No permissions match your search.
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                  {roleForm.permissions.length === 0 && (
                    <div className="px-4 py-2 text-sm text-red-500 border-t">
                      Please select at least one permission.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowCreateRoleModal(false)}
                  className="px-5 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={createRole}
                  disabled={creatingRole}
                  className={`px-5 py-2 rounded-lg text-white ${creatingRole ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  {creatingRole ? "Creating..." : "Create Role"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Role Permissions Modal */}
        {showPermissionsModal && selectedRolePermissions && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6">
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedRolePermissions.name}
                  </h2>
                  <p className="text-gray-500 mt-1">
                    {selectedRolePermissions.description || "No description"}
                  </p>
                </div>
                <button
                  onClick={() => setShowPermissionsModal(false)}
                  className="text-xl"
                >
                  ✕
                </button>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                        Permission
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedRolePermissions.permissions || []).map(
                      (permId) => {
                        const perm = allPermissions.find(
                          (p) => p.permission_code === permId,
                        );
                        const formattedPermission = (
                          perm?.permission_code || permId
                        )
                          .replace(/_/g, " ")
                          .toLowerCase()
                          .replace(/\b[a-z]/g, (char) => char.toUpperCase());

                        return (
                          <tr key={permId} className="border-b border-gray-100">
                            <td className="px-4 py-3 text-sm font-medium text-gray-800">
                              {formattedPermission}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {perm?.description || "—"}
                            </td>
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Edit Role Modal */}
        {showEditRoleModal && editingRole && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Edit Role & Description
                  </h2>
                  <p className="text-gray-500 mt-1">
                    Update the role name and description. Permissions aren't
                    changed here.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowEditRoleModal(false);
                    setEditingRole(null);
                  }}
                  className="text-xl"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role Name *
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={editRoleForm.name}
                    onChange={(e) =>
                      setEditRoleForm({ ...editRoleForm, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={editRoleForm.description}
                    onChange={(e) =>
                      setEditRoleForm({
                        ...editRoleForm,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editRoleForm.isActive}
                    onChange={(e) =>
                      setEditRoleForm({
                        ...editRoleForm,
                        isActive: e.target.checked,
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Active
                  </span>
                </label>
                <p className="text-xs text-gray-400">
                  {editingRole.permissions?.length ?? 0} permission
                  {(editingRole.permissions?.length ?? 0) === 1 ? "" : "s"}{" "}
                  assigned — unchanged by this form.
                </p>
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowEditRoleModal(false);
                    setEditingRole(null);
                  }}
                  className="px-5 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={updateRole}
                  disabled={updatingRole}
                  className={`px-5 py-2 rounded-lg text-white ${updatingRole ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  {updatingRole ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Role Modal */}
        {showDeleteRoleModal && roleToDelete && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-gray-900">Delete Role</h2>
              <p className="mt-4 text-gray-600">Do you want to delete</p>
              <p className="font-semibold text-lg mt-2">
                "{roleToDelete.name}" ?
              </p>
              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={() => {
                    setShowDeleteRoleModal(false);
                    setRoleToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  No
                </button>
                <button
                  onClick={confirmDeleteRole}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
          theme="colored"
        />
      </div>
    </>
  );
};

export default TenantUsers;
